import {
  type FaceDefinition,
  type ProductMeasurementKind,
  SURFACE_TYPES,
  type SurfaceType,
  surfaceKindMismatch,
} from '@repo/api-contracts';

/** 이 화면이 만들지 못하는 표면 타입 — 계약의 flat·cylinder 가 아닌 타입 전부다(지금은 mesh). 타입이 늘면 같이 잠긴다. */
type SurfaceReason = Exclude<SurfaceType, 'flat' | 'cylinder'>;

/**
 * 이 화면에서 편집할 수 없는 사유. 표면 사유는 이 화면이 만들지 못하는 표면 타입이고,
 * kind-mismatch 는 상품 실측 종류와 맞지 않는 표면이 든 정의다(평면 상품의 cylinder, 원통 상품의 flat·mesh).
 */
export type UnsupportedReason = SurfaceReason | 'kind-mismatch';

const SURFACE_REASONS = SURFACE_TYPES.filter(
  (type): type is SurfaceReason => type !== 'flat' && type !== 'cylinder',
);
/** 사유별 툴바 안내와 저장 거부 문구. */
const REASON_MESSAGES: Readonly<
  Record<UnsupportedReason, { readonly notice: string; readonly save: string }>
> = {
  'kind-mismatch': {
    notice:
      '실측 종류와 맞지 않는 표면이 든 정의입니다. 이 화면에서는 편집·사진 업로드·저장을 할 수 없고, 저장 버전 탐색은 가능합니다.',
    save: '실측 종류와 맞지 않는 표면이 든 정의는 현재 화면에서 저장할 수 없습니다.',
  },
  mesh: {
    notice:
      'Mesh가 포함된 정의입니다. 이 화면에서는 편집·사진 업로드·저장을 지원하지 않습니다. 원본은 보존되며 저장 버전 탐색은 가능합니다.',
    save: 'Mesh가 포함된 정의는 현재 화면에서 저장할 수 없습니다.',
  },
};
/** 사유가 여럿이면 실측 종류 불일치를 먼저 알린다 — 표면을 고쳐도 이 상품에는 저장할 수 없다. */
const NOTICE_ORDER: readonly UnsupportedReason[] = ['kind-mismatch', ...SURFACE_REASONS];

/** 정의의 표면 타입과 상품 실측 종류에서 편집을 잠그는 사유를 모은다. 표면 사유가 먼저 온다. */
export const unsupportedReasonsOf = (
  faces: Readonly<Record<string, Pick<FaceDefinition, 'surfaces'> | undefined>>,
  measurementKind: ProductMeasurementKind,
): readonly UnsupportedReason[] => {
  const types = new Set(
    Object.values(faces).flatMap((face) => face?.surfaces.map((surface) => surface.type) ?? []),
  );
  const reasons: UnsupportedReason[] = SURFACE_REASONS.filter((type) => types.has(type));
  const mismatched = [...types].some((type) => surfaceKindMismatch(type, measurementKind) !== null);
  return mismatched ? [...reasons, 'kind-mismatch'] : reasons;
};

const leadingReason = (reasons: readonly UnsupportedReason[]): UnsupportedReason | undefined =>
  NOTICE_ORDER.find((reason) => reasons.includes(reason));

/** 툴바에 띄우는 운영자 안내. 사유가 없으면 null. */
export const unsupportedDefinitionNotice = (
  reasons: readonly UnsupportedReason[],
): string | null => {
  const reason = leadingReason(reasons);
  return reason === undefined ? null : REASON_MESSAGES[reason].notice;
};

/** 저장 조립·저장 요청이 쓰는 짧은 거부 문구. 사유가 없으면 null. */
export const unsupportedSaveMessage = (reasons: readonly UnsupportedReason[]): string | null => {
  const reason = leadingReason(reasons);
  return reason === undefined ? null : REASON_MESSAGES[reason].save;
};
